import { useQuery } from "@tanstack/react-query";

import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { LoadingSpinner } from "@vega-core/components/Loaders/LoadingSpinner/LoadingSpinner";
import { ActionaryModal } from "@vega-core/components/Modals/ActionaryModal/ActionaryModal";
import { Notice, NoticeType } from "@vega-core/components/Notice/Notice";
import { m } from "@vega-core/paraglide/messages";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";

import { draftEmailPreviewQueryOptions } from "./draftEmailQueries";

type PreviewEmailModalProps = {
  dealId: string;
  dealShortlistItemId: string;
  body: string;
  isOpen: boolean;
  onClose: () => void;
};

export function PreviewEmailModal({
  dealId,
  dealShortlistItemId,
  body,
  isOpen,
  onClose,
}: PreviewEmailModalProps) {
  const previewQuery = useQuery(
    draftEmailPreviewQueryOptions({
      dealId,
      dealShortlistItemId,
      body: isOpen ? body : undefined,
    }),
  );

  const previewWidthClasses = "w-[37.5rem] max-w-[calc(100vw-6rem)]";

  return (
    <ActionaryModal
      dataTestId="preview-email-modal"
      fluidHeight
      fluidWidth
      open={isOpen}
      setOpen={(open) => {
        if (!open) onClose();
      }}
      title={m.coInvestments_draftEmail_previewTitle()}
    >
      {previewQuery.isPending ? (
        <div
          className={cnTwMerge(
            "flex min-h-40 items-center justify-center",
            previewWidthClasses,
          )}
        >
          <LoadingSpinner />
        </div>
      ) : null}

      {previewQuery.isError ? (
        <div className={previewWidthClasses}>
          <Notice
            action={
              <ThemedButton
                dataTestId="preview-email-retry"
                isLoading={previewQuery.isFetching}
                onClick={() => previewQuery.refetch()}
                type="button"
                variant="text"
              >
                {m.coInvestments_draftEmail_previewRetry()}
              </ThemedButton>
            }
            content={m.coInvestments_draftEmail_previewError()}
            dataTestId="preview-email-error"
            type={NoticeType.ERROR}
          />
        </div>
      ) : null}

      {previewQuery.isSuccess ? (
        <iframe
          className={cnTwMerge(
            "h-[70dvh] rounded border border-border/primary bg-background/primary",
            previewWidthClasses,
          )}
          data-testid="preview-email-frame"
          sandbox="allow-popups allow-popups-to-escape-sandbox"
          srcDoc={previewQuery.data}
          title={m.coInvestments_draftEmail_previewTitle()}
        />
      ) : null}
    </ActionaryModal>
  );
}
